import { DeviceTelemetryType } from '@signageos/common-types/dist/Device/Telemetry/DeviceTelemetryType';
import { call, fork } from 'redux-saga/effects';
import { buffers, channel } from 'redux-saga';
import { AssignedRunners } from '../../../Runner/runnerSaga';
import { BrowserRuntime } from '../../../CustomScripts/Runtimes/browserRuntime';
import Responsibility from '../../../Feature/Responsibility';
import ManagementCapability from '../../../NativeDevice/Management/ManagementCapability';
import Property from '../../../Property/Property';
import { createDeviceTelemetrySaga } from '../Telemetry/deviceTelemetrySagas';
import IGetTelemetryProperties from '../Telemetry/IGetTelemetryProperties';
import { IPropertyStorage } from '../../../Property/propertyStorage';
import IManagementDriver from '../../../NativeDevice/Management/IManagementDriver';

// Constants for runner telemetry configuration
const RUNNER_WAIT_TIMEOUT = 2000;
const RUNNER_RETRY_COUNT = 20;
const RUNNER_RETRY_DELAY = 200;

/**
 * Utility function to get assigned runners from property storage
 */
function* getAssignedRunners(propertyStorage: IPropertyStorage): any {
	const assignedRunners: AssignedRunners = yield propertyStorage.getValueOrDefault(Property.DEVICE_RUNNERS, {});
	return assignedRunners;
}

/**
 * Utility function to get runner iframe window
 */
function getRunnerIframeWindow(iframeId: string): { iframe: HTMLIFrameElement | null; iframeWindow: any } {
	const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
	const iframeWindow = iframe?.contentWindow as any;
	return { iframe, iframeWindow };
}

/**
 * Utility function to wait for sosRunner to be available in iframe
 */
function* waitForSosRunner(iframeId: string, runnerUid: string): any {
	const { iframeWindow } = getRunnerIframeWindow(iframeId);

	if (!iframeWindow) {
		throw new Error(`Iframe window not found for runner ${runnerUid}`);
	}

	const waitForModule = (retries = RUNNER_RETRY_COUNT): Promise<any> => {
		return new Promise((resolve, reject) => {
			const checkModule = () => {
				if (iframeWindow && iframeWindow.sosRunner && iframeWindow.sosRunner.get) {
					resolve(iframeWindow.sosRunner);
				} else if (retries > 0) {
					setTimeout(checkModule, RUNNER_RETRY_DELAY);
					retries--;
				} else {
					reject(new Error(`sosRunner not available for runner ${runnerUid}`));
				}
			};
			checkModule();
		});
	};

	return yield call(waitForModule);
}

/**
 * Utility function to create and run runner iframe
 */
function* createAndRunRunner(
	browserRuntime: BrowserRuntime,
	managementDriver: IManagementDriver,
	runner: AssignedRunners[string],
	iframeId: string,
): any {
	const messageChannel = channel<MessageEvent>(buffers.dropping(100));
	const messagesHandler = (event: MessageEvent) => {
		messageChannel.put(event);
	};
	const telemetry = (messageResult: string) => parent.postMessage({ telemetryData: messageResult, iframeId }, '*');

	yield call(
		browserRuntime.runPluginInBrowser,
		managementDriver,
		runner,
		runner.configuration || {},
		runner.runnerFile,
		runner.appletJsFile,
		messagesHandler,
		telemetry,
	);

	yield fork(browserRuntime.bindCustomScriptsMessages, messageChannel, window, iframeId);
	yield new Promise((resolve) => setTimeout(resolve, RUNNER_WAIT_TIMEOUT));

	return { messageChannel };
}

/**
 * Utility function to get telemetry data from runner with management driver
 */
function* getRunnerTelemetryDataWithManagement(runner: AssignedRunners[string], managementDriver: IManagementDriver): any {
	const iframeId = runner.md5Checksum;
	let { iframe } = getRunnerIframeWindow(iframeId);

	try {
		// Create iframe if it doesn't exist
		if (!iframe) {
			const browserRuntime = new BrowserRuntime();
			yield* createAndRunRunner(browserRuntime, managementDriver, runner, iframeId);
			iframe = getRunnerIframeWindow(iframeId).iframe;
		}

		if (!iframe?.contentWindow) {
			return {
				runnerUid: runner.runnerUid!,
				version: runner.runnerVersion!,
				data: { error: 'Iframe not found' },
			};
		}

		const sosRunner = yield* waitForSosRunner(iframeId, runner.runnerUid!);
		const telemetryData = yield call(sosRunner.get);

		return {
			runnerUid: runner.runnerUid!,
			version: runner.runnerVersion!,
			data: telemetryData || {},
		};
	} catch (error) {
		console.error(`Failed to get telemetry for runner ${runner.runnerUid}:`, error);
		return {
			runnerUid: runner.runnerUid!,
			version: runner.runnerVersion!,
			data: { error: 'Failed to get telemetry', details: error.message },
		};
	}
}

export const telemetryRunnerSaga = createDeviceTelemetrySaga(
	DeviceTelemetryType.RUNNER,
	Responsibility.RUNNERS,
	[ManagementCapability.RUNNERS],
	getRunnerSettings,
);

export function* getRunnerSettings({
	propertyStorage,
	managementDriver,
}: IGetTelemetryProperties & { propertyStorage: IPropertyStorage; managementDriver: IManagementDriver }): any {
	try {
		const assignedRunners = yield* getAssignedRunners(propertyStorage);
		const runnerEntries = Object.entries(assignedRunners);

		if (runnerEntries.length === 0) {
			return [];
		}

		const runnerDataList: { runnerUid: string; version: string; data: object }[] = [];

		for (const [_assignmentUid, runner] of runnerEntries) {
			const typedRunner = runner as AssignedRunners[string];

			try {
				const telemetryResult = yield* getRunnerTelemetryDataWithManagement(typedRunner, managementDriver);
				runnerDataList.push(telemetryResult);
			} catch (error) {
				runnerDataList.push({
					runnerUid: typedRunner.runnerUid!,
					version: typedRunner.runnerVersion!,
					data: { error: 'Failed to process runner', details: error.message },
				});
			}
		}

		return runnerDataList.length > 0 ? runnerDataList : [];
	} catch (error) {
		console.error('Failed to get runner settings for telemetry:', error);
		return [];
	}
}
